import {
  alertProvenance,
  closesBadge,
  newRaiseProvenance,
  perkLeft,
  perkSpoken,
  seenLabel,
} from '../alerts';
import type { SampleDeal } from '../data';
import { cd, spoken } from '../format';
import { paceKind } from '../momentum';
import { now, onTick } from './clock';
import { $, $$, attr, segmented, tablist, text } from './dom';
import { track } from './events';
import { inView, reducedMotion } from './motion';

/* -------------------------------------------------------------------------
 * "What an alert looks like": six alert types, the chosen one's sample
 * email beside them. Once the section is on screen it plays through the
 * types, 8 s each, the chosen row's bar counting the time down. It holds
 * under the pointer, while focus is inside it, while the tab is hidden and
 * while it is scrolled away, and stops for good when a type is chosen by
 * click or key, or a change of pace over the Momentum email. Under reduced
 * motion it never plays. Every countdown in the emails ticks on the page
 * clock, whichever email is showing.
 * ---------------------------------------------------------------------- */

/** One live value in an email, by its data-tick. */
function paint(el: HTMLElement, deal: SampleDeal, n: number): void {
  switch (el.dataset.tick) {
    case 'cd':
      text(el, cd(deal.closesAt - n));
      attr(el, 'aria-label', spoken(deal.closesAt - n));
      break;
    case 'badge': {
      const b = closesBadge(deal, n);
      text($(el, '[data-glyph]'), b.glyph);
      text($(el, '[data-left]'), b.left);
      attr(el, 'data-urgency', b.urgency);
      break;
    }
    case 'perk':
      text(el, perkLeft(deal, n));
      attr(el, 'aria-label', perkSpoken(deal, n));
      break;
    case 'prov':
      text(el, alertProvenance(deal, n));
      break;
    case 'prov-new':
      text(el, newRaiseProvenance(deal, n));
      break;
  }
}

export function alertTypes(root: HTMLElement, deals: SampleDeal[]): void {
  const list = $(root, '[role="tablist"]');
  const tabs = $$(root, '[role="tab"]');
  const panel = $(root, '[role="tabpanel"]');
  const emails = $$(root, '[data-alert-email]');
  const cta = $(root, '[data-alert-cta]');
  const counter = $(root, '[data-alert-seen]');
  if (!list || !panel || tabs.length === 0) return;

  let active = Math.max(
    0,
    tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'),
  );
  const seen = new Set([active]);
  let auto = !reducedMotion();
  let started = false;

  // Each reason to hold the bar where it is.
  const holds = {
    hovered: false,
    focused: false,
    hidden: document.hidden,
    away: false,
  };
  const hold = (reason: keyof typeof holds, on: boolean) => {
    holds[reason] = on;
    root.classList.toggle('is-paused', Object.values(holds).some(Boolean));
  };

  // Restart the chosen row's bar from empty, or clear it for good.
  const time = () => {
    tabs.forEach((t) => t.classList.remove('is-timing'));
    if (!auto || !started) return;
    void tabs[active].offsetWidth;
    tabs[active].classList.add('is-timing');
  };

  const show = (index: number) => {
    active = index;
    const type = tabs[index].dataset.alertType;
    for (const email of emails) email.hidden = email.dataset.alertType !== type;
    attr(panel, 'aria-labelledby', tabs[index].id);
    seen.add(index);
    $(tabs[index], '[data-at-unseen]')?.remove();
    text(counter, seenLabel(seen.size, tabs.length));
    cta?.classList.toggle('is-ready', seen.size === tabs.length);
    time();
  };

  const select = tablist(
    list,
    (index) => {
      auto = false;
      show(index);
    },
    { manual: true },
  );

  // Momentum's three emails, one shown, picked by the segments over them.
  const momentum = $(root, '[data-mo-emails]');
  const pace = momentum && $(momentum, '[role="group"]');
  if (momentum && pace) {
    segmented(pace, (value) => {
      const kind = paceKind(value);
      if (!kind) return;
      auto = false;
      time();
      for (const email of $$(momentum, '[data-mo-email]')) {
        email.hidden = email.dataset.kind !== kind;
      }
      track('landing_momentum_picked', { scenario: kind, from: 'alert' });
    });
  }

  for (const tab of tabs) {
    $(tab, '.at-prog')?.addEventListener('animationend', (e) => {
      if (!auto) return;
      // A bar that ran in no time (a stylesheet that cuts animations short
      // rather than removing them) would spin through the types: stop.
      if (e.elapsedTime < 1) {
        auto = false;
        time();
        return;
      }
      const next = (active + 1) % tabs.length;
      select(next);
      show(next);
    });
  }

  root.addEventListener('pointerenter', () => hold('hovered', true));
  root.addEventListener('pointerleave', () => hold('hovered', false));
  root.addEventListener('focusin', () => hold('focused', true));
  root.addEventListener('focusout', (e) => {
    const to = e.relatedTarget;
    if (!(to instanceof Node && root.contains(to))) hold('focused', false);
  });
  document.addEventListener('visibilitychange', () =>
    hold('hidden', document.hidden),
  );
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) =>
      hold('away', !entries.some((e) => e.isIntersecting)),
    ).observe(root);
  }
  inView(root, () => {
    started = true;
    time();
  });

  const tickers = $$(root, '[data-tick]').flatMap((el) => {
    const deal = deals.find((d) => d.id === el.dataset.deal);
    return deal ? [{ el, deal }] : [];
  });
  const tick = (n: number) => {
    for (const { el, deal } of tickers) paint(el, deal, n);
  };
  tick(now());
  onTick(tick);
}
